import React, { Suspense, useCallback } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { AppContextProvider } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';

import ArticleClusterBlock from './Blocks/ArticleClusterBlock';
import DeepResearchBlock from './Blocks/DeepResearchBlock';
import ExpertsBlock from './Blocks/ExpertsBlock';
import HTMLBlock from './Blocks/HTMLBlock';
import HeroBlock from './Blocks/HeroBlock';
import HorizontalRuleBlock from './Blocks/HorizontalRuleBlock';
import ImageBlock from './Blocks/ImageBlock';
import NewsletterBlock from './Blocks/NewsletterBlock';
import QuoteBlock from './Blocks/QuoteBlock';
import RecommendedArticlesBlock from './Blocks/RecommendedArticleBlock';
import RenderTiptapContent from './Blocks/RenderTiptapContent';
import SectionBlock from './Blocks/SectionBlock';
import SubscribeButtonBlock from './Blocks/SubscribeButtonBlock';
import SubscribeMatrixBlock from './Blocks/SubscribeMatrixBlock';
import TITVBlock from './Blocks/TITVBlock';
import TextBlock from './Blocks/TextBlock';
import VideoBlock from './Blocks/VideoBlock';
import { BlockTypes } from './Blocks/constants';
import WithAnalyticsWrapper from './WithAnalyticsWrapper';
import { blocksWithoutContent } from './constants';
import type { PageTemplateJSON, PageTemplateProps, RecursiveValue } from './interfaces';
import './styles.scss';

const queryClient = new QueryClient();

const PageTemplates = ({
  articleClusters,
  recommendedArticles,
  redirectTo,
  reporters,
  singleOffer,
  templateJSON,
  templateMetadata,
  titvEpisodes,
  ...props
}: PageTemplateProps) => {
  const { Track } = useEventTracking({
    ...(templateMetadata?.page_analytics ? { page: templateMetadata.page_analytics } : {}),
  });

  const RenderedTemplate = useCallback((blocks: PageTemplateJSON[]) => {
    if (blocks.length === 0) return;

    return blocks?.map((block) => {
      const contentBlock =
        (block.content?.json !== undefined
          ? block.content.json
          : block?.children
          ? block.children
          : block?.content) ?? {};
      const config = block?.config;
      const type = block.type;

      if (!Object.keys(contentBlock).length && !blocksWithoutContent.includes(type as BlockTypes)) {
        return null;
      }

      switch (type) {
        case BlockTypes.Image:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <ImageBlock
                config={config}
                content={contentBlock as Record<string, RecursiveValue>}
              />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.Section:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <SectionBlock config={config}>
                {RenderedTemplate(contentBlock as PageTemplateJSON[])}
              </SectionBlock>
            </WithAnalyticsWrapper>
          );
        case BlockTypes.TextContainer:
          // Don't wrap in WithAnalyticsWrapper because it's already wrapped in RenderTiptapContent
          return (
            <TextBlock key={block.id} config={config}>
              <RenderTiptapContent
                config={config}
                contentBlock={contentBlock as Record<string, RecursiveValue>}
              />
            </TextBlock>
          );
        case BlockTypes.Hero:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <HeroBlock
                config={config}
                content={contentBlock as Record<string, RecursiveValue>}
                offer={singleOffer}
              />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.Video:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <VideoBlock
                config={config}
                content={contentBlock as Record<string, RecursiveValue>}
              />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.SubscribeButton:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <SubscribeButtonBlock config={config} offer={singleOffer} redirectTo={redirectTo} />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.ArticleCluster:
          // Tracking done on the component itself
          return (
            <ArticleClusterBlock
              key={block.id}
              articleClusters={articleClusters[block.id]}
              config={config}
            />
          );
        case BlockTypes.HTML:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <HTMLBlock html={(contentBlock as Record<string, RecursiveValue>).html as string} />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.DeepResearch:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <DeepResearchBlock
                config={config}
                content={contentBlock as Record<string, RecursiveValue>}
              />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.Newsletter:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <NewsletterBlock
                config={config}
                content={contentBlock as Record<string, RecursiveValue>}
              />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.SubscribeMatrix:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <SubscribeMatrixBlock config={config} offers={block.offers} redirectTo={redirectTo} />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.RecommendedArticles:
          // Tracking done on the component itself
          return (
            <RecommendedArticlesBlock
              key={block.id}
              config={config}
              recommendedArticles={recommendedArticles}
            />
          );
        case BlockTypes.Quote:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <QuoteBlock
                config={config}
                content={contentBlock as Record<string, RecursiveValue>}
              />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.Experts:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <ExpertsBlock config={config} reporters={reporters} />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.Titv:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <TITVBlock config={config} episode={titvEpisodes?.[block.id]} />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.HorizontalRule:
          return <HorizontalRuleBlock key={block.id} config={config} />;
        default:
          return (
            <React.Fragment key={block.id}>
              <RenderTiptapContent
                config={config}
                contentBlock={contentBlock as Record<string, RecursiveValue>}
              />
            </React.Fragment>
          );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppContextProvider {...props}>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<div>Loading…</div>}>
          <Track>
            <main className="relative px-16 pt-56 sm:px-28 sm:pt-64">
              <div className="m-auto max-w-container-lg">
                {RenderedTemplate(templateJSON.blocks)}
              </div>
            </main>
          </Track>
        </Suspense>
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export default PageTemplates;
